<template>
  <div ref="mapEl" class="wirepas-route-map" />
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, onUnmounted, watch } from 'vue'
import maplibregl from 'maplibre-gl'
import 'maplibre-gl/dist/maplibre-gl.css'
import { wirepasTypeColor, WIREPAS_CURRENT_COLOR } from '@/utils/wirepas'

export default defineComponent({
  name: 'WirepasRouteMap',
  props: {
    legs: { type: Array as () => any[], required: true },
  },
  setup(props) {
    const mapEl = ref<HTMLElement | null>(null)
    let map: maplibregl.Map | null = null
    let markers: maplibregl.Marker[] = []
    let popups: maplibregl.Popup[] = []
    let mapLoaded = false

    const colorOf = wirepasTypeColor

    // Rebuilds markers/route on the already-loaded map. Does NOT touch the Map instance itself —
    // tearing down and recreating the whole WebGL map on every legs update (e.g. from redundant
    // parent re-fetches) can race the style's own 'load' handshake and leave the map blank forever.
    const renderLegs = () => {
      if (!map || !mapLoaded) return
      markers.forEach((m) => m.remove())
      markers = []
      popups.forEach((p) => p.remove())
      popups = []
      if (map.getLayer('route-line')) map.removeLayer('route-line')
      if (map.getSource('route')) map.removeSource('route')

      const withCoords = props.legs.filter((l) => l.lat && l.lng)
      const coords: [number, number][] = withCoords.map((l) => [parseFloat(l.lng), parseFloat(l.lat)])

      if (coords.length > 1) {
        map.addSource('route', {
          type: 'geojson',
          data: { type: 'Feature', geometry: { type: 'LineString', coordinates: [...coords].reverse() }, properties: {} },
        })
        map.addLayer({
          id: 'route-line',
          type: 'line',
          source: 'route',
          layout: { 'line-join': 'round', 'line-cap': 'round' },
          paint: { 'line-color': '#8F8F8C', 'line-width': 2.5, 'line-opacity': 0.7, 'line-dasharray': [2, 2] },
        })
      }

      // Unique gateways (latest leg per gw wins for "current")
      const currentGwId = props.legs[0]?.gwId
      const seen = new Set<string>()
      withCoords.forEach((leg) => {
        if (seen.has(leg.gwId)) return
        seen.add(leg.gwId)
        const isCurrent = leg.gwId === currentGwId && !leg.until
        const visits = props.legs.filter((l) => l.gwId === leg.gwId).length
        const color = isCurrent ? WIREPAS_CURRENT_COLOR : colorOf(leg.locationType)
        const s = isCurrent ? 22 : 14

        const el = document.createElement('div')
        // Must be absolute, not relative — MapLibre positions markers purely via CSS transform;
        // an in-flow element stacks below any earlier marker instead of anchoring to its own coordinate.
        el.style.cssText = `position:absolute;width:${s}px;height:${s}px;cursor:pointer`

        if (isCurrent) {
          const ring = document.createElement('div')
          ring.className = 'wrm-pulse-ring'
          ring.style.background = color
          el.appendChild(ring)
        }

        const dot = document.createElement('div')
        dot.style.cssText = `position:absolute;inset:0;border-radius:50%;background:${color};border:3px solid #fff;box-shadow:0 1px 5px rgba(0,0,0,.4)`
        el.appendChild(dot)

        const popupEl = document.createElement('div')
        popupEl.className = 'wrm-popup'
        const title = document.createElement('div')
        title.className = 'wrm-popup__title'
        title.textContent = leg.locationName ?? leg.gwId
        popupEl.appendChild(title)
        if (isCurrent) {
          const now = document.createElement('div')
          now.className = 'wrm-popup__meta'
          now.textContent = '📍 tu teraz'
          popupEl.appendChild(now)
        }
        const visitsEl = document.createElement('div')
        visitsEl.className = 'wrm-popup__meta'
        visitsEl.textContent = `Ilość wizyt: ${visits}`
        popupEl.appendChild(visitsEl)

        const popup = new maplibregl.Popup({ offset: 12, closeButton: false, closeOnClick: false }).setDOMContent(popupEl)
        popups.push(popup)
        const marker = new maplibregl.Marker({ element: el, anchor: 'center' })
          .setLngLat([parseFloat(leg.lng), parseFloat(leg.lat)])
          .addTo(map!)
        el.addEventListener('mouseenter', () => popup.setLngLat(marker.getLngLat()).addTo(map!))
        el.addEventListener('mouseleave', () => popup.remove())
        markers.push(marker)
      })

      if (coords.length > 1) {
        const bounds = coords.reduce((b, c) => b.extend(c), new maplibregl.LngLatBounds(coords[0], coords[0]))
        map!.fitBounds(bounds, { padding: 60, maxZoom: 16 })
      } else if (coords.length === 1) {
        map!.setCenter(coords[0])
      }
    }

    const buildMap = () => {
      if (!mapEl.value || map) return

      const withCoords = props.legs.filter((l) => l.lat && l.lng)
      const center: [number, number] = withCoords.length
        ? [parseFloat(withCoords[0].lng), parseFloat(withCoords[0].lat)]
        : [20.0, 52.0]

      map = new maplibregl.Map({
        container: mapEl.value,
        style: 'https://tiles.openfreemap.org/styles/liberty',
        center,
        zoom: 12,
      })

      map.addControl(new maplibregl.NavigationControl(), 'top-right')
      map.on('load', () => {
        mapLoaded = true
        renderLegs()
      })
    }

    watch(() => props.legs, renderLegs, { deep: true })
    onMounted(buildMap)
    onUnmounted(() => { map?.remove(); map = null; mapLoaded = false })

    return { mapEl }
  },
})
</script>

<style scoped>
.wirepas-route-map {
  width: 100%;
  height: 340px;
  border-radius: 12px;
  overflow: hidden;
}
</style>

<style>
/* Unscoped: markers/popups are plain DOM nodes created in JS, outside Vue's scoped template tree.
   Selectors are prefixed with .wirepas-route-map so this doesn't leak onto other maps in the app. */
.wrm-pulse-ring {
  position: absolute;
  inset: 0;
  border-radius: 50%;
  animation: wrm-pulse 1.8s ease-out infinite;
  pointer-events: none;
}

@keyframes wrm-pulse {
  0%   { transform: scale(1); opacity: 0.55; }
  100% { transform: scale(2.4); opacity: 0; }
}

.wirepas-route-map .maplibregl-popup-tip {
  display: none;
}

.wirepas-route-map .maplibregl-popup-content {
  background: var(--card-bg, #fff);
  border-radius: 10px;
  padding: 8px 12px;
  box-shadow: var(--shadow, 0 4px 16px rgba(0, 0, 0, 0.18));
  border: 1px solid var(--border-color, rgba(0, 0, 0, 0.08));
}

.wrm-popup__title {
  font-weight: 700;
  font-size: 13px;
  color: var(--text-color, #252422);
  white-space: nowrap;
}

.wrm-popup__meta {
  font-size: 11px;
  color: var(--text-muted, #888);
  margin-top: 2px;
  white-space: nowrap;
}
</style>
